import { useCallback } from 'react';
import {
  type SequenceXmpMetadataInput,
  useVersionAssetMutation,
} from '@frameio/graphql-client/generated';
import { useActiveAccountId, useFinderRefetch } from '@frameio/shared/contexts';
import { useResourceNotFoundToast } from '../../../../shared/components/ExportLocation';
import { useLastExportAssetData } from '../../../hooks/useLastExportAssetData';
import { useRemoveExportMetadata } from '../../../hooks/useRemoveExportMetadata';
import { useSaveExportMetadata } from '../../../hooks/useSaveExportMetadata';
import type { ExportProgressItem } from '../../../types/exportProgressTypes';
import TEXT from './generated/TEXT/useExportCompleteCallback';
import { useCreateCommentsFromMarkers } from './useCreateCommentsFromMarkers/useCreateCommentsFromMarkers';

TEXT.DEFINE({
  versionStackNotFound: {
    /** Notification title when the version stack is not found */
    title: 'Version stack not found.',
    /** Notification details when the version stack is not found */
    details: 'Exporting this sequence as a new asset.',
  },
});

type ExportCompleteCallbackParams = {
  exportProgressItem: ExportProgressItem;
  exportMarkersAsCommentsEnabled: boolean;
};

export const useExportCompleteCallback = (isVersionStackMode: boolean) => {
  const activeAccountId = useActiveAccountId();
  const refetchFinder = useFinderRefetch();
  const saveExportMetadata = useSaveExportMetadata();
  const lastExportAssetData = useLastExportAssetData();
  const showResourceNotFoundToast = useResourceNotFoundToast();

  const { createCommentsFromMarkers } = useCreateCommentsFromMarkers();

  const removeExportMetadata = useRemoveExportMetadata();

  const lastExportAssetId = isVersionStackMode
    ? lastExportAssetData?.id
    : undefined;

  const lastExportVersionStackId =
    isVersionStackMode &&
    lastExportAssetData?.parent?.__typename === 'VersionStackAsset'
      ? lastExportAssetData.parent.id
      : undefined;

  const [versionStackAssetMutation] = useVersionAssetMutation();
  const createExportVersion = useCallback(
    async (assetId: string) => {
      const versionId = lastExportVersionStackId ?? lastExportAssetId ?? '';
      const { data } = await versionStackAssetMutation({
        variables: {
          input: {
            firstAssetId: assetId,
            secondAssetId: versionId,
          },
        },
        onError: (error) => {
          if (error.message.includes('not found')) {
            showResourceNotFoundToast({
              title: TEXT.versionStackNotFound.title,
              details: TEXT.versionStackNotFound.details,
            });
          }
        },
      });

      const versionStack = data?.versionAsset?.versionStackAsset;
      const topAssetId = versionStack?.versions?.[0]?.id ?? '';

      return {
        versionId: versionStack?.id ?? '',
        topAssetId,
      };
    },
    [
      versionStackAssetMutation,
      showResourceNotFoundToast,
      lastExportVersionStackId,
      lastExportAssetId,
    ]
  );

  return useCallback(
    async ({
      exportProgressItem,
      exportMarkersAsCommentsEnabled,
    }: ExportCompleteCallbackParams) => {
      const firstAssetId = exportProgressItem.assetId ?? '';
      const projectId = exportProgressItem.projectId ?? '';

      const xmpMetadata: SequenceXmpMetadataInput = {
        assetId: firstAssetId,
        projectId,
        accountId: activeAccountId,
      };

      let assetId = firstAssetId;

      if (isVersionStackMode) {
        const versionStack = await createExportVersion(firstAssetId);

        assetId = versionStack.topAssetId;
        xmpMetadata.versionId = versionStack.versionId;
        await removeExportMetadata({
          variables: {
            assetId: lastExportAssetId ?? '',
            sequenceId: exportProgressItem.sequenceId,
          },
        });
      }

      if (exportMarkersAsCommentsEnabled) {
        await createCommentsFromMarkers({
          ...exportProgressItem,
          assetId,
        });
      }

      saveExportMetadata({
        assetId,
        sequenceId: exportProgressItem.sequenceId,
        includeInOutTimestamps: !exportProgressItem.fullSequence,
        xmpMetadata,
      });

      refetchFinder({});
    },
    [
      activeAccountId,
      isVersionStackMode,
      saveExportMetadata,
      refetchFinder,
      createCommentsFromMarkers,
      createExportVersion,
      removeExportMetadata,
      lastExportAssetId,
    ]
  );
};
